import { z } from 'zod'
import { paginacaoSchema } from './comum'
import { juntarCamposDaQuery } from './contatos'
import { itemVendaSchema, somarItens } from './vendas'
// [INÍCIO]/[FIM] ADR 0080
import type { NivelDeAdesao, PerfilDoLead } from '../perfil-lead'

/**
 * Negociações (4.3 e 4.4).
 *
 * Um ciclo de venda. O contato é permanente e pode ter vários ao longo do
 * tempo — é isso que separa este modelo do anterior, em que funil, etapa e
 * valor moravam no próprio contato e cada venda nova apagava a anterior.
 *
 * O ciclo é a unidade que interessa medir: ticket e tempo de fechamento só
 * existem com um registro por compra.
 */

export const STATUS_NEGOCIACAO = ['aberta', 'ganha', 'perdida'] as const
export type StatusNegociacao = (typeof STATUS_NEGOCIACAO)[number]

export const ROTULO_STATUS_NEGOCIACAO: Record<StatusNegociacao, string> = {
  aberta: 'Em aberto',
  ganha: 'Ganha',
  perdida: 'Perdida',
}

export const criarNegociacaoSchema = z.object({
  contatoId: z.string().uuid(),
  funilId: z.string().uuid(),
  /** Sem etapa, entra na primeira do funil. */
  etapaId: z.string().uuid().optional().nullable(),
  titulo: z.string().trim().max(160).optional().nullable(),
  valor: z.number().min(0).max(9_999_999_999).optional().nullable(),
})
export type CriarNegociacaoEntrada = z.infer<typeof criarNegociacaoSchema>

export const atualizarNegociacaoSchema = z.object({
  titulo: z.string().trim().max(160).optional().nullable(),
  valor: z.number().min(0).max(9_999_999_999).optional().nullable(),
})
export type AtualizarNegociacaoEntrada = z.infer<typeof atualizarNegociacaoSchema>

/**
 * Mover de etapa. Ganho e perda exigem o registro correspondente (ADR 0002):
 * sem ele o histórico do ciclo some, e é o histórico que sustenta o relatório.
 */
export const moverNegociacaoSchema = z.object({
  etapaId: z.string().uuid(),
  ganho: z
    .object({
      /**
       * Itens da venda (10.3). Vazio = ganho registrado só com valor.
       *
       * Cada item é o que foi vendido, digitado (não há catálogo de produtos,
       * ADR 0054), e precisa de descrição: sem ela a venda fica com uma linha
       * sem nome no relatório.
       */
      itens: z.array(itemVendaSchema).default([]),
      /** Desconto editável sobre a soma dos itens (10.4). */
      desconto: z.number().min(0).max(9_999_999).default(0),
      /** Usado quando não há itens. */
      valorTotal: z.number().min(0).max(9_999_999_999).optional(),
      dataGanho: z.coerce.date().optional(),
    })
    /**
     * Sem itens e sem valor, a venda entrava como zero.
     *
     * Pior: o valor estimado que a negociação já tinha era sobrescrito por
     * esse zero. O ganho virava uma linha de R$ 0,00 no relatório, e ninguém
     * desconfiava porque a tela tinha dito "ganho registrado".
     */
    .refine((g) => (g.itens?.length ?? 0) > 0 || g.valorTotal !== undefined, {
      message: 'informe os itens vendidos ou o valor total',
      path: ['valorTotal'],
    })
    /**
     * Desconto maior que o subtotal também virava R$ 0,00, por causa do
     * `Math.max(0, ...)` que evita venda negativa. O grampo continua lá, mas
     * o caso agora é recusado: desconto acima do total é erro de digitação, e
     * gravar zero em silêncio esconde o erro dentro do faturamento.
     */
    .refine(
      (g) => {
        const itens = g.itens ?? []
        if (itens.length === 0) return true
        return (g.desconto ?? 0) <= somarItens(itens)
      },
      { message: 'o desconto não pode passar do total dos itens', path: ['desconto'] },
    )
    .optional(),
  perda: z
    .object({
      motivoId: z.string().uuid(),
      observacao: z.string().trim().max(2000).optional(),
    })
    .optional(),
})
export type MoverNegociacaoEntrada = z.infer<typeof moverNegociacaoSchema>

/**
 * O quadro mostra só as abertas por padrão.
 *
 * Ganhas e perdidas continuam existindo e aparecem no histórico do card; se
 * ficassem no quadro, as colunas de desfecho cresceriam para sempre e o funil
 * deixaria de responder "o que está em jogo agora".
 */
export const filtroNegociacoesSchema = z.preprocess(juntarCamposDaQuery, paginacaoSchema.extend({
  funilId: z.string().uuid().optional(),
  etapaId: z.string().uuid().optional(),
  contatoId: z.string().uuid().optional(),
  vendedorId: z.string().uuid().optional(),
  status: z.enum(['aberta', 'ganha', 'perdida', 'todas']).default('aberta'),
  busca: z.string().trim().max(120).optional(),
  /*
    Cada id precisa ser uuid de verdade.

    Sem isto, "tagIds=lixo" chegava até o Prisma e voltava 500 — a resposta
    errada para entrada malformada, e uma que não diz a quem chamou o que
    consertar.
  */
  tagIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : v.split(',')))
    .pipe(z.array(z.string().uuid()).optional()),
  /*
    Campo personalizado do contato, de `campos[chave]=valor`, como em Contatos. O painel Filtros
    de Leads oferecia os campos e contava o filtro como ligado, mas a rota não os recebia: o quadro
    continuava mostrando todo mundo.
  */
  campos: z.record(z.string(), z.string()).optional(),
}))
export type FiltroNegociacoes = z.infer<typeof filtroNegociacoesSchema>

export interface NegociacaoItemVendaDto {
  descricao: string
  quantidade: number
  precoUnitario: number
}

/** O que aconteceu no fim do ciclo, para a aba Histórico contar a história. */
export interface DesfechoDto {
  valorBruto: number
  desconto: number
  valorTotal: number
  itens: NegociacaoItemVendaDto[]
  motivo: string | null
  observacao: string | null
}

export interface NegociacaoDto {
  id: string
  contatoId: string
  contatoNome: string
  /** Já formatado ou já mascarado pelo servidor. Nunca o número cru. */
  contatoTelefone: string | null
  telefoneMascarado: boolean
  titulo: string | null
  funilId: string
  funilNome: string
  etapaId: string
  etapaNome: string
  etapaCor: string
  etapaTipo: 'normal' | 'ganho' | 'perda'
  valor: number | null
  status: StatusNegociacao
  abertaEm: string
  /** Quando entrou na etapa atual: a base do "parado há N dias" do quadro. */
  etapaDesde: string
  /**
   * Tem tarefa pendente marcada para o contato.
   *
   * Falso é o sinal de lead sem próximo passo — o que as Estatísticas contam
   * como "sem tarefa" e o quadro não mostrava em card nenhum.
   */
  temTarefa: boolean
  fechadaEm: string | null
  /** Dono vem da carteira do contato: a negociação não tem dono próprio. */
  vendedorId: string | null
  vendedorNome: string | null
  tags: { id: string; nome: string; cor: string }[]
  /** Preenchido só em negociação fechada. */
  desfecho: DesfechoDto | null
}

/** Resumo por etapa, para o cabeçalho de cada coluna do quadro. */
export interface ResumoEtapaNegociacao {
  etapaId: string
  negociacoes: number
  valorTotal: number
}

// [INÍCIO] Visão Resumos de Leads e adesão da persona (ADR 0080)
/**
 * Entre as negociações já fechadas da mesa cujo lead tem o mesmo perfil,
 * quantas terminaram em matrícula. `nivel` diz quão estreito foi o "mesmo
 * perfil" que achou base suficiente.
 */
export interface AdesaoDaPersonaDto {
  /** 0 a 1. */
  taxa: number
  casos: number
  ganhas: number
  nivel: NivelDeAdesao
}

/** Uma negociação aberta com o último resumo da conversa do contato. */
export interface ResumoDeLeadDto {
  negociacao: NegociacaoDto
  chamadoId: string | null
  /** Texto multi-linha do resumo (primeira linha é o resumo em si). */
  resumo: string | null
  resumidoEm: string | null
  matricula: 'sim' | 'nao' | 'indefinido' | null
  perfil: PerfilDoLead | null
  veioDoPortal: boolean
  adesao: AdesaoDaPersonaDto | null
}

export interface ResumosDeLeadsDto {
  itens: ResumoDeLeadDto[]
  /** Negociações abertas no filtro, inclusive as que ainda não têm resumo. */
  total: number
  /** Quantas negociações fechadas com perfil sustentam o índice. */
  baseDaAdesao: number
}
// [FIM] Visão Resumos de Leads e adesão da persona

/** Uma linha do histórico de movimentações de uma negociação. */
export interface MovimentoDto {
  id: string
  deEtapaNome: string | null
  paraEtapaNome: string
  usuarioNome: string | null
  criadoEm: string
}

/**
 * Mexer em vários cards de uma vez.
 *
 * Fora da lista de propósito: **mover de etapa**. Mudar etapa dispara ganho e
 * perda, que pedem valor e motivo negociação por negociação — em massa, dez
 * vendas nasceriam com o mesmo número e o relatório de faturamento passaria a
 * mentir.
 */
/**
 * O que os ids do lote representam.
 *
 * O quadro seleciona cards, que são negociações; a lista seleciona linhas, que
 * são contatos. A ação é a mesma nos dois casos, porque transferir dono e
 * etiquetar valem para a carteira e não para o ciclo — o que muda é só o
 * caminho até o contato. O padrão é `negociacao` para o quadro, que veio
 * primeiro, continuar chamando sem passar nada.
 */
export const ALVO_EM_MASSA = ['negociacao', 'contato'] as const
export type AlvoEmMassa = (typeof ALVO_EM_MASSA)[number]

/**
 * Quantos ids cabem num pedido de ação em massa. Seleção maior (o "Marcar
 * tudo" dos Contatos) é repartida pela tela em pedidos deste tamanho.
 */
export const MAXIMO_POR_PEDIDO_EM_MASSA = 200

const loteBase = {
  ids: z.array(z.string().uuid()).min(1).max(MAXIMO_POR_PEDIDO_EM_MASSA),
  alvo: z.enum(ALVO_EM_MASSA).default('negociacao'),
}

export const acaoEmMassaSchema = z.discriminatedUnion('acao', [
  z.object({
    acao: z.literal('transferir'),
    ...loteBase,
    vendedorId: z.string().uuid(),
  }),
  /**
   * Devolver ao rodízio.
   *
   * O caso que pediu isto é o vendedor que sai da empresa: a carteira dele
   * precisa voltar para a fila, e transferir para outra pessoa só troca o
   * problema de dono. Sem dono, o rodízio distribui como faz com cliente novo.
   */
  z.object({
    acao: z.literal('tirar_dono'),
    ...loteBase,
  }),
  z.object({
    acao: z.literal('etiquetar'),
    ...loteBase,
    tagId: z.string().uuid(),
  }),
  /** O contrário de etiquetar. Marcar em lote sem poder desmarcar em lote
   *  transformava um clique errado em trabalho manual de cem linhas. */
  z.object({
    acao: z.literal('desetiquetar'),
    ...loteBase,
    tagId: z.string().uuid(),
  }),
  /**
   * Tirar todas as etiquetas dos selecionados de uma vez.
   *
   * Para recomeçar a classificação de um grupo sem ter de saber, lead por
   * lead, quais etiquetas cada um carrega. As etiquetas continuam existindo;
   * só deixam de estar nestes contatos.
   */
  z.object({
    acao: z.literal('limpar_etiquetas'),
    ...loteBase,
  }),
])
export type AcaoEmMassaEntrada = z.infer<typeof acaoEmMassaSchema>
export type NomeDaAcaoEmMassa = AcaoEmMassaEntrada['acao']

/**
 * O texto de cada ação, num lugar só.
 *
 * A tela mostra o mesmo verbo no seletor, na confirmação e no aviso de
 * resultado. Escrito três vezes à mão, um dia diverge e a confirmação passa a
 * prometer uma coisa diferente da que o botão faz.
 */
export const ACOES_EM_MASSA: {
  acao: NomeDaAcaoEmMassa
  /** O nome completo da ação, para explicar o que ela é. */
  rotulo: string
  /**
   * O verbo curto, para caber num botão.
   *
   * "Transferir para" e "Tirar o dono (devolver ao rodízio)" nasceram para ler
   * bem antes de um seletor. Num botão, o primeiro fica pendurado sem
   * complemento e o segundo estica a janela inteira.
   */
  verbo: string
  /** O que o complemento pede, ou `null` quando a ação não precisa de nenhum. */
  complemento: 'vendedor' | 'tag' | null
  /** Frase da confirmação, com `{n}` no lugar de "3 leads" e `{alvo}` no complemento. */
  confirmacao: string
  /** Ação que tira coisa pede um botão de perigo, não o azul de sempre. */
  destrutiva: boolean
}[] = [
  {
    acao: 'transferir',
    verbo: 'Transferir',
    rotulo: 'Transferir para',
    complemento: 'vendedor',
    confirmacao: 'Passar {n} para {alvo}. O dono anterior deixa de ver esses clientes.',
    destrutiva: false,
  },
  {
    acao: 'tirar_dono',
    verbo: 'Deixar sem responsável',
    rotulo: 'Tirar o dono (devolver ao rodízio)',
    complemento: null,
    confirmacao:
      'Deixar {n} sem dono. Eles voltam para o rodízio e podem cair com qualquer pessoa do setor.',
    destrutiva: true,
  },
  {
    acao: 'etiquetar',
    verbo: 'Etiquetar',
    rotulo: 'Adicionar etiqueta',
    complemento: 'tag',
    confirmacao: 'Marcar {n} com a etiqueta {alvo}. Quem já tem continua com uma só.',
    destrutiva: false,
  },
  {
    acao: 'desetiquetar',
    verbo: 'Remover etiqueta',
    rotulo: 'Remover etiqueta',
    complemento: 'tag',
    confirmacao: 'Tirar a etiqueta {alvo} de {n}. Quem não tem a etiqueta fica como está.',
    destrutiva: true,
  },
  {
    acao: 'limpar_etiquetas',
    verbo: 'Remover todas',
    rotulo: 'Remover todas as etiquetas',
    complemento: null,
    confirmacao:
      'Tirar todas as etiquetas de {n}. As etiquetas continuam existindo; só saem destes leads.',
    destrutiva: true,
  },
]
